import styled, { keyframes } from "styled-components";

// Modern animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    transform: translateX(-10px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

export const TableContainerMain = styled.div<{
  tableHeight?: string;
  isGetAll?: boolean;
  padding?: number;
}>`
    width: 100%;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    background: ${({theme}) => theme.custom?.background?.main || '#ffffff'};
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    animation: ${fadeIn} 0.4s ease-out;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
    
    // Enhanced mobile responsiveness
    @media (max-width: 768px) {
        border-radius: 12px;
        margin: 8px;
        box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
        
        &:hover {
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
        }
    }
    
    @media (max-width: 480px) {
        border-radius: 8px;
        margin: 4px;
        box-shadow: 0 1px 8px rgba(0, 0, 0, 0.04);
        
        &:hover {
            box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
        }
    }

    .tableHead {
        background: linear-gradient(135deg, ${({theme}) => theme.custom?.background?.main || '#ffffff'} 0%, ${({theme}) => theme.custom?.background?.secondary || '#f8fafc'} 100%);
        padding: 20px 24px;
        border-radius: 16px 16px 0 0;
        border-bottom: 1px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
    }

    .grid-container > * {
        color: ${({theme}) => theme.custom?.primary?.dark || '#1e293b'} !important;
        font-weight: 500;
        width: 100%;
        overflow: auto;
        background-color: ${({theme}) => theme.custom?.background?.main || '#ffffff'};
        border-radius: 0 0 16px 16px;
    }

    .actions {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 12px;
    }

    .grid-container {
        width: 100%;
        height: ${({isGetAll, tableHeight}) =>
                tableHeight
                        ? tableHeight
                        : isGetAll
                                ? "calc(100vh - 104px)"
                                : "calc(100vh - 62px - 105px)"};
        position: relative;
    }
    
    .table-header {
        text-transform: uppercase;
        letter-spacing: 0.5px;
        font-weight: 600;
    }

    .row-hover {
        transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
        
        &:hover {
            cursor: pointer;
            background: linear-gradient(135deg, ${({theme}) => theme.palette?.info?.light || '#dbeafe'} 0%, ${({theme}) => theme.palette?.info?.main || '#3b82f6'} 100%) !important;
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
        }
    }

    .header-flex {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .table_header {
        background: linear-gradient(135deg, ${({theme}) => theme.custom?.background?.main || '#ffffff'} 0%, ${({theme}) => theme.custom?.background?.secondary || '#f8fafc'} 100%);
        padding: 20px 24px;
        position: sticky;
        top: 0;
        z-index: 10;
        border-radius: 16px 16px 0 0;
        backdrop-filter: blur(10px);
        border-bottom: 1px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        animation: ${slideIn} 0.3s ease-out;

        .title {
            color: ${({theme}) => theme.custom?.primary?.dark || '#1e293b'};
            font-weight: 700;
            font-size: 1.5rem;
            background: linear-gradient(135deg, ${({theme}) => theme.custom?.primary?.main || '#3b82f6'} 0%, ${({theme}) => theme.custom?.primary?.dark || '#1e40af'} 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    }

    .row-back-red {
        background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
        border-left: 4px solid #ef4444;

        &:hover {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.1) 100%) !important;
            transform: translateX(4px);
        }
    }

    .row-back-blue {
        background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%);
        border-left: 4px solid #3b82f6;

        &:hover {
            background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%) !important;
            transform: translateX(4px);
        }
    }

    .row-back-green {
        background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(21, 128, 61, 0.05) 100%);
        border-left: 4px solid #22c55e;

        &:hover {
            background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(21, 128, 61, 0.1) 100%) !important;
            transform: translateX(4px);
        }
    }

    .row-back-white {
        background: linear-gradient(135deg, rgba(248, 250, 252, 0.8) 0%, rgba(241, 245, 249, 0.6) 100%);
        border-left: 4px solid #e2e8f0;

        &:hover {
            background: linear-gradient(135deg, rgba(248, 250, 252, 1) 0%, rgba(241, 245, 249, 0.8) 100%) !important;
            transform: translateX(4px);
        }
    }

    .row-back-yellow {
        background: linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%);
        border-left: 4px solid #fbbf24;

        &:hover {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15) 0%, rgba(245, 158, 11, 0.1) 100%) !important;
            transform: translateX(4px);
        }
    }

    // Enhanced DataGrid styling
    .MuiDataGrid-root {
        border: none !important;
        border-radius: 0 0 16px 16px;
        
        .MuiDataGrid-columnHeaders {
            background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
            border-bottom: 2px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-size: 0.875rem;
        }
        
        .MuiDataGrid-columnHeader {
            display: flex !important;
            align-items: center !important;
        }

        .MuiDataGrid-row {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            
            &:hover {
                background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
            }

            &:nth-of-type(even) {
                background: rgba(248, 250, 252, 0.5);
            }
        }

        .MuiDataGrid-cell {
            border-bottom: 1px solid ${({theme}) => theme.custom?.border?.light || '#f1f5f9'} !important;
            padding: 16px !important;
            font-size: 0.875rem;
            line-height: 1.5;
            display: flex !important;
            align-items: center !important;
        }

        .MuiDataGrid-cell:focus {
            outline: none !important;
        }

        .MuiDataGrid-cell:focus-within {
            outline: 2px solid ${({theme}) => theme.custom?.primary?.main || '#3b82f6'} !important;
            outline-offset: -2px;
        }
        
        // Enhanced mobile responsiveness
        @media (max-width: 768px) {
            .MuiDataGrid-columnHeaders {
                font-size: 0.75rem;
                padding: 8px 12px;
            }
            
            .MuiDataGrid-cell {
                padding: 12px 8px !important;
                font-size: 0.75rem;
            }
            
            .MuiDataGrid-row {
                min-height: 48px;
            }
        }
        
        @media (max-width: 480px) {
            .MuiDataGrid-columnHeaders {
                font-size: 0.7rem;
                padding: 6px 8px;
            }
            
            .MuiDataGrid-cell {
                padding: 8px 6px !important;
                font-size: 0.7rem;
            }
            
            .MuiDataGrid-row {
                min-height: 40px;
            }
        }
    }

    // Loading skeleton
    .skeleton-row {
        background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
        background-size: 200px 100%;
        animation: ${shimmer} 1.5s infinite;
        height: 60px;
        margin: 8px 0;
        border-radius: 8px;
    }

    // Enhanced pagination
    .pagination-container {
        background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        border-top: 1px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
        padding: 16px 24px;
        border-radius: 0 0 16px 16px;
    }
`;

export const Spinner = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%);
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  border-radius: 16px;
  animation: ${fadeIn} 0.3s ease-out;
`;

// Enhanced Loading Skeleton Component
export const SkeletonLoader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  
  .skeleton-header {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: ${shimmer} 1.5s infinite;
    height: 40px;
    border-radius: 8px;
    margin-bottom: 16px;
  }
  
  .skeleton-row {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: ${shimmer} 1.5s infinite;
    height: 60px;
    border-radius: 8px;
    
    &:nth-child(even) {
      animation-delay: 0.1s;
    }
    
    &:nth-child(odd) {
      animation-delay: 0.2s;
    }
  }
`;

// Enhanced Toolbar Component
export const EnhancedToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-bottom: 1px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
  border-radius: 16px 16px 0 0;
  
  .toolbar-left {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  
  .toolbar-right {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  
  .search-container {
    position: relative;
    min-width: 300px;
    
    .search-input {
      background: white;
      border: 2px solid ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
      border-radius: 12px;
      padding: 12px 16px 12px 48px;
      font-size: 0.875rem;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      
      &:focus {
        border-color: ${({theme}) => theme.custom?.primary?.main || '#3b82f6'};
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        outline: none;
      }
      
      &::placeholder {
        color: #94a3b8;
      }
    }
    
    .search-icon {
      position: absolute;
      left: 16px;
      top: 50%;
      transform: translateY(-50%);
      color: #94a3b8;
      font-size: 1.125rem;
    }
  }
`;

// Enhanced Action Button
export const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  ${({ variant = 'primary' }) => {
    switch (variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          
          &:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
          }
        `;
      case 'secondary':
        return `
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          color: #475569;
          
          &:hover {
            background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(71, 85, 105, 0.2);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          
          &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
          }
        `;
      default:
        return '';
    }
  }}
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  .button-icon {
    font-size: 1rem;
  }
  
  .button-text {
    font-weight: 500;
  }
`;

// Enhanced Status Badge
export const StatusBadge = styled.span<{ status: 'success' | 'warning' | 'error' | 'info' | 'neutral' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  ${({ status }) => {
    switch (status) {
      case 'success':
        return `
          background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
          color: #166534;
          border: 1px solid #86efac;
        `;
      case 'warning':
        return `
          background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
          color: #92400e;
          border: 1px solid #fbbf24;
        `;
      case 'error':
        return `
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #991b1b;
          border: 1px solid #f87171;
        `;
      case 'info':
        return `
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          color: #1e40af;
          border: 1px solid #93c5fd;
        `;
      case 'neutral':
        return `
          background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
          color: #475569;
          border: 1px solid #cbd5e1;
        `;
      default:
        return '';
    }
  }}
`;

// Enhanced Empty State
export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 16px;
  border: 2px dashed ${({theme}) => theme.custom?.border?.light || '#e2e8f0'};
  
  .empty-icon {
    font-size: 4rem;
    color: #cbd5e1;
    margin-bottom: 16px;
    animation: ${pulse} 2s infinite;
  }
  
  .empty-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
  }
  
  .empty-description {
    font-size: 0.875rem;
    color: #94a3b8;
    max-width: 400px;
    line-height: 1.5;
  }
  
  .empty-action {
    margin-top: 24px;
  }
`;
